import { useState, useEffect, useCallback } from 'react';
import { ScanResult, STORAGE_KEYS, API_CONFIG } from '../types';

/**
 * Custom hook for managing scan history with session storage persistence.
 */
export function useScanHistory() {
  const [scanHistory, setScanHistory] = useState<ScanResult[]>([]);

  // Load scan history from session storage on mount
  useEffect(() => {
    const storedHistory = sessionStorage.getItem(STORAGE_KEYS.SCAN_HISTORY);
    if (storedHistory) {
      try {
        const parsedHistory = JSON.parse(storedHistory);
        setScanHistory(parsedHistory);
      } catch (err) {
        console.error('Error parsing scan history from session storage:', err);
      }
    }
  }, []);

  // Update session storage when scan history changes
  useEffect(() => {
    if (scanHistory.length > 0) {
      sessionStorage.setItem(STORAGE_KEYS.SCAN_HISTORY, JSON.stringify(scanHistory));
    }
  }, [scanHistory]);

  /**
   * Add a scan result to history.
   * Automatically trims to MAX_HISTORY_LENGTH.
   */
  const addToHistory = useCallback((scan: ScanResult) => {
    setScanHistory(prev => {
      const newHistory = [scan, ...prev];
      return newHistory.slice(0, API_CONFIG.MAX_HISTORY_LENGTH);
    });
  }, []);

  /**
   * Clear all scan history.
   */
  const clearHistory = useCallback(() => {
    setScanHistory([]);
    sessionStorage.removeItem(STORAGE_KEYS.SCAN_HISTORY);
  }, []);

  /**
   * Get a scan by ID.
   */
  const getScanById = useCallback((id: number): ScanResult | undefined => {
    return scanHistory.find(scan => scan.id === id);
  }, [scanHistory]);

  return {
    scanHistory,
    addToHistory,
    clearHistory,
    getScanById,
    setScanHistory,
  };
}
